import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ChevronLeft, ChevronDown, ChevronRight, ShieldOff, ShieldCheck, KeyRound, Trash2, Users, Mail, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PlanBadge from "@/components/PlanBadge";

/**
 * Super-Admin Clubs management.
 *
 * Lists every tenant on the platform with quick counts, lets the SA
 * create a new club (name + slug + initial back-office passcode), and
 * deactivate / reactivate clubs. Deactivated clubs cannot be logged
 * into via the back-office passcode flow.
 */
export default function SuperAdminClubsPage() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState({ name: "", slug: "", contact_email: "", city: "", initial_passcode: "" });
  const [busy, setBusy] = useState(false);
  // iter72i · Lazy-loaded roster cache keyed by club_id.
  const [expanded, setExpanded] = useState(new Set());
  const [rosters, setRosters] = useState({}); // {club_id: {loading, members, error}}

  const toggleRoster = async (clubId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(clubId)) next.delete(clubId);
      else next.add(clubId);
      return next;
    });
    if (rosters[clubId]?.members || rosters[clubId]?.loading) return;
    setRosters((r) => ({ ...r, [clubId]: { loading: true } }));
    try {
      const { data } = await api.get(`/super-admin/clubs/${clubId}/members-lite`);
      setRosters((r) => ({ ...r, [clubId]: { loading: false, members: data.members || [] } }));
    } catch (e) {
      setRosters((r) => ({
        ...r,
        [clubId]: { loading: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message },
      }));
    }
  };

  const reload = () => {
    setLoading(true);
    api
      .get("/super-admin/clubs")
      .then((r) => setClubs(r.data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/super-admin/clubs", form);
      setCreated(data);
      setForm({ name: "", slug: "", contact_email: "", city: "", initial_passcode: "" });
      setShowCreate(false);
      reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (clubId, deactivate) => {
    const verb = deactivate ? "deactivate" : "reactivate";
    if (!window.confirm(`${verb === "deactivate" ? "Deactivate" : "Reactivate"} this club?`)) return;
    setBusy(true);
    try {
      await api.post(`/super-admin/clubs/${clubId}/${verb}`);
      reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteClub = async (club) => {
    // Type-the-slug confirmation: irreversible, wipes every tenant-scoped
    // record. Mirrors the GitHub repo-delete UX so a stray keystroke can't
    // wipe a tenant.
    const typed = window.prompt(
      `PERMANENT DELETE — this wipes ${club.name}, every order, wishlist, photo, audit log, and solo-club member account.\n\n` +
      `Type the club slug "${club.slug}" to confirm:`,
    );
    if (typed === null) return; // cancelled
    if (typed.trim().toLowerCase() !== (club.slug || "").toLowerCase()) {
      window.alert("Slug doesn't match. Nothing was deleted.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete(`/super-admin/clubs/${club.club_id}`, {
        params: { confirm: club.slug },
      });
      window.alert(
        `${club.name} deleted.\n\n` +
        `Purges: ${JSON.stringify(data.purges)}`,
      );
      reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen" data-testid="sa-clubs-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <Link
          to="/super-admin"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#1A422D] hover:underline"
        >
          <ChevronLeft size={14} /> Back to Dashboard
        </Link>

        <div className="mt-4 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="overline">Tenants</div>
            <h1 className="font-serif text-4xl text-[#1A1A1A] mt-1">Clubs</h1>
            <p className="text-[#4A4A4A] mt-2 max-w-2xl text-sm">
              Every club on the platform. Each has its own back-office passcode and complete data isolation — no read path leaks between tenants.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setShowCreate(true); setCreated(null); }}
            data-testid="sa-clubs-create-button"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={16} /> Add Club
          </button>
        </div>

        {error && (
          <div className="mt-4 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
        )}
        {created && (
          <div className="mt-4 px-4 py-3 text-sm border border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]">
            <strong>Created:</strong> {created.name} (slug <code>{created.slug}</code>) — share the passcode with their pro shop.
          </div>
        )}

        {showCreate && (
          <section className="mt-6 surface-card p-6 sm:p-8" data-testid="sa-clubs-create-form">
            <div className="overline">New Tenant</div>
            <h2 className="font-serif text-2xl mt-1">Add a Club</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
              <Field label="Club name" value={form.name} onChange={(v) => setForm({ ...form, name: v })}
                     placeholder="e.g. Cypress Point Club" testId="sa-clubs-name" />
              <Field label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })}
                     hint="lowercase, used internally (e.g. cypress-point)"
                     placeholder="cypress-point" testId="sa-clubs-slug" />
              <Field label="Contact email" value={form.contact_email}
                     onChange={(v) => setForm({ ...form, contact_email: v })}
                     placeholder="proshop@club.com" testId="sa-clubs-contact" />
              <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })}
                     placeholder="Pebble Beach, CA" testId="sa-clubs-city" />
              <Field label="Initial back-office passcode"
                     value={form.initial_passcode}
                     onChange={(v) => setForm({ ...form, initial_passcode: v })}
                     hint="5-10 chars. Share this securely with the pro shop on first login."
                     placeholder="e.g. cypress01"
                     testId="sa-clubs-passcode" />
            </div>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary text-xs">Cancel</button>
              <button
                type="button"
                onClick={create}
                disabled={busy || !form.name || !form.slug}
                data-testid="sa-clubs-save"
                className="btn-primary"
              >
                {busy ? "Creating…" : "Create Club"}
              </button>
            </div>
          </section>
        )}

        <div className="mt-8 surface-card overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F3F1E9]">
              <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                <th className="px-6 py-4">Club</th>
                <th className="px-6 py-4">Slug</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Members</th>
                <th className="px-6 py-4 text-right">Active Orders</th>
                <th className="px-6 py-4 text-right">Wishlist</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-6 py-10 text-center text-[#7A7A7A]">Loading…</td></tr>}
              {!loading && clubs.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-[#7A7A7A]">No clubs yet.</td></tr>
              )}
              {clubs.map((c) => {
                const active = c.status === "active";
                const isOpen = expanded.has(c.club_id);
                const roster = rosters[c.club_id];
                return (
                  <React.Fragment key={c.club_id}>
                  <tr className="border-t border-[#E5E3D8]" data-testid={`sa-club-row-${c.slug}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          onClick={() => toggleRoster(c.club_id)}
                          data-testid={`sa-club-expand-${c.slug}`}
                          className="mt-1 text-[#7A7A7A] hover:text-[#1A422D]"
                          aria-label={isOpen ? "Collapse members" : "Expand members"}
                        >
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                        <div>
                          <div className="font-serif text-lg text-[#1A1A1A]">{c.name}</div>
                          <div className="text-xs text-[#7A7A7A]">{c.city || "—"}</div>
                          {c.plan && <div className="mt-1.5"><PlanBadge plan={c.plan} /></div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-[#1A422D]">{c.slug}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border ${
                        active
                          ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                          : "border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33]"
                      }`}>{active ? "Active" : "Deactivated"}</span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm">{c.member_count}</td>
                    <td className="px-6 py-4 text-right text-sm">{c.active_order_count}</td>
                    <td className="px-6 py-4 text-right text-sm">{c.wishlist_count}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-4">
                        <Link
                          to={`/super-admin/clubs/${c.club_id}`}
                          data-testid={`sa-club-open-${c.slug}`}
                          className="text-xs text-[#1A422D] underline underline-offset-4 inline-flex items-center gap-1.5"
                        >
                          Open
                        </Link>
                        <button
                          type="button"
                          onClick={() => setStatus(c.club_id, active)}
                          disabled={busy}
                          data-testid={`sa-club-toggle-${c.slug}`}
                          className="text-xs text-[#1A422D] underline underline-offset-4 inline-flex items-center gap-1.5"
                        >
                          {active ? <ShieldOff size={12} /> : <ShieldCheck size={12} />}
                          {active ? "Deactivate" : "Reactivate"}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteClub(c)}
                          disabled={busy}
                          data-testid={`sa-club-delete-${c.slug}`}
                          className="text-xs text-[#8a1a1a] underline underline-offset-4 inline-flex items-center gap-1.5 hover:opacity-80"
                          title={`Permanently delete ${c.name} and all its data`}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-[#E5E3D8] bg-[#FBF9F1]" data-testid={`sa-club-roster-${c.slug}`}>
                      <td colSpan={7} className="px-8 py-5">
                        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.22em] text-[#8a6f33]">
                          <Users size={12} />
                          Members ({roster?.members?.length ?? c.member_count ?? 0}) · Alphabetical
                        </div>
                        {roster?.loading && (
                          <div className="text-xs text-[#7A7A7A]">Loading members…</div>
                        )}
                        {roster?.error && (
                          <div className="text-xs text-[#8a1a1a]">{roster.error}</div>
                        )}
                        {roster?.members && roster.members.length === 0 && (
                          <div className="text-xs text-[#7A7A7A]">No members on the roster yet.</div>
                        )}
                        {roster?.members && roster.members.length > 0 && (
                          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm"
                              data-testid={`sa-club-roster-list-${c.slug}`}>
                            {roster.members.map((m) => (
                              <li key={m.user_id}
                                  className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E5E3D8]">
                                <div className="min-w-0 flex-1">
                                  <div className="text-[#1A422D] truncate">{m.name || "—"}</div>
                                  <div className="text-[10px] text-[#7A7A7A] truncate inline-flex items-center gap-1">
                                    <Mail size={9} /> {m.email}
                                    {m.club_member_number && <span>· #{m.club_member_number}</span>}
                                  </div>
                                </div>
                                {m.activated ? (
                                  <span
                                    className="text-[9px] uppercase tracking-[0.16em] text-[#1A422D] inline-flex items-center gap-1 shrink-0"
                                    title="Member has activated their account"
                                  >
                                    <CheckCircle2 size={9} /> Active
                                  </span>
                                ) : (
                                  <span
                                    className="text-[9px] uppercase tracking-[0.16em] text-[#8a6f33] shrink-0"
                                    title="Invited but not yet activated"
                                  >
                                    Pending
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-xs text-[#7A7A7A] inline-flex items-center gap-2">
          <KeyRound size={12} /> Pro shop staff sign in by typing their club&rsquo;s passcode on the Staff page &mdash; no club picker required.
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, hint, testId }) {
  return (
    <div>
      <label className="label-elegant">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="input-field w-full mt-1.5"
      />
      {hint && <p className="text-[11px] text-[#7A7A7A] mt-1">{hint}</p>}
    </div>
  );
}
